/**
 * GraphLightsView: the category's pattern graph as lights at night (Documentation/specs/2026-11-03-graph-lights-view-plan.md,
 * sections 1-12).
 *
 * A full-viewport overlay opened from the ActionBar's "Graph view" button, bound to the chat's live diag_state. Loads ONLY the
 * current diagnosis category bundle (GET /api/v5/brain/category, useCategoryBundle); the lights are computed client-side
 * from each tse:diag_state (lightsFromState), so a new turn re-lights the map with no extra request.
 * Header: title, category, stage caption, view options (edges, labels, facts, ruled out, theme), fit, close. Body: the canvas
 * and a side panel (status, search, top causes, legend; the selected node's or edge's details). Focus is trapped; Esc clears
 * the selection, then closes.
 *
 * Props: diagState (the latest tse:diag_state, with a category), sessionId, onClose.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LocateFixed, Moon, Sun, X } from "lucide-react";

import { useFocusTrap, useMediaQuery } from "../panelDomHooks";
import useCategoryBundle, { learningKeyOf } from "../useCategoryBundle";
import { DEFAULT_OPTIONS, EDGE_MODES, THEME_MODES } from "./lightsConstants";
import { layoutCategory } from "./lightsLayout";
import { lightsFromState } from "./lightsState";
import { LIGHTS_TEXT, canvasLabel, formatPercent, stageCaption } from "./lightsText";
import LightsCanvas from "./LightsCanvas";
import LightsSidebar from "./LightsSidebar";
import "./graphLights.css";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const ESCAPE_KEY = "Escape";
const ICON_PX = 14;
const TOGGLES = Object.freeze(["labels", "facts", "embers"]);

function LiveMirror({ summary }) {
  return (
    <ul className="d5-lights-sr" aria-live="polite" aria-label={LIGHTS_TEXT.liveLabel}>
      {summary.leading.map((c) => <li key={c.nid}>{`${c.label} ${formatPercent(c.pct)}`}</li>)}
    </ul>
  );
}

function EdgeModeControl({ value, onChange }) {
  return (
    <div className="d5-lights-seg" role="radiogroup" aria-label={LIGHTS_TEXT.edges}>
      <span className="d5-lights-seg-label">{LIGHTS_TEXT.edges}</span>
      {Object.values(EDGE_MODES).map((mode) => (
        <button key={mode} type="button" role="radio" aria-checked={value === mode} className="d5-lights-seg-btn" onClick={() => onChange(mode)}>
          {LIGHTS_TEXT.edgeModes[mode]}
        </button>
      ))}
    </div>
  );
}

function ToggleControl({ name, checked, onChange }) {
  return (
    <button type="button" className="d5-lights-toggle" aria-pressed={checked} onClick={() => onChange(name, !checked)}>
      {LIGHTS_TEXT.toggles[name]}
    </button>
  );
}

export default function GraphLightsView({ diagState, sessionId, onClose }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const controlsRef = useRef(null);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  useFocusTrap(dialogRef, true);

  const learnedKey = learningKeyOf(diagState);
  const bundle = useCategoryBundle(sessionId, diagState?.diagnosis_id, diagState?.category, learnedKey);
  const layout = useMemo(() => (bundle ? layoutCategory(bundle) : null), [bundle]);
  const lights = useMemo(() => (bundle ? lightsFromState(bundle, diagState) : null), [bundle, diagState]);
  const hasNodes = !!layout && layout.positions.size > 0;
  const categoryLabel = bundle?.label || diagState?.category || "";

  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [themeMode, setThemeMode] = useState(THEME_MODES.NIGHT);
  // {node: nid} | {edge: id} | null
  const [selection, setSelection] = useState(null);
  const [hoverId, setHoverId] = useState(null);

  // A new bundle (category or diagnosis change) drops the selection: its node or edge may not exist any more.
  useEffect(() => { setSelection(null); setHoverId(null); }, [layout]);

  const setOption = useCallback((name, value) => setOptions((prev) => ({ ...prev, [name]: value })), []);
  const select = useCallback((next) => {
    if (next?.node && lights?.nodes.has(next.node)) {
      setSelection({ node: next.node });
      controlsRef.current?.flyTo(next.node);
      return;
    }
    const edge = next?.edge && lights?.edgeById.get(next.edge);
    if (edge && layout) {
      setSelection({ edge: edge.id });
      const a = layout.positions.get(edge.src);
      const b = layout.positions.get(edge.dst);
      if (a && b) controlsRef.current?.flyTo({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      return;
    }
    setSelection(null);
  }, [lights, layout]);

  // Focus never leaves the dialog (useFocusTrap), so Esc always bubbles to the overlay: it clears a selection first, then closes.
  const onKeyDown = (e) => {
    if (e.key !== ESCAPE_KEY) return;
    e.stopPropagation();
    if (selection) { setSelection(null); return; }
    onClose();
  };
  const otherTheme = themeMode === THEME_MODES.NIGHT ? THEME_MODES.DAY : THEME_MODES.NIGHT;

  const tree = (
    <div className="d5-lights-overlay" data-theme={themeMode} onKeyDown={onKeyDown}>
      <div className="d5-lights-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef} tabIndex={-1}>
        <header className="d5-lights-header">
          <h2 className="d5-lights-title" id={titleId}>
            {LIGHTS_TEXT.title}
            {categoryLabel && <span className="d5-lights-category">{categoryLabel}</span>}
          </h2>
          {lights && hasNodes && <span className="d5-lights-caption">{stageCaption(lights.stage, lights.summary)}</span>}
          <span className="d5-lights-spacer" />
          <div className="d5-lights-controls" role="group" aria-label={LIGHTS_TEXT.controlsLabel}>
            <EdgeModeControl value={options.edgeMode} onChange={(mode) => setOption("edgeMode", mode)} />
            {TOGGLES.map((name) => <ToggleControl key={name} name={name} checked={options[name]} onChange={setOption} />)}
            <button type="button" className="d5-lights-btn" onClick={() => setThemeMode(otherTheme)} title={LIGHTS_TEXT.themeHint}
              aria-label={`${LIGHTS_TEXT.themeHint}: ${LIGHTS_TEXT.themes[otherTheme]}`}>
              {themeMode === THEME_MODES.NIGHT ? <Sun size={ICON_PX} aria-hidden="true" /> : <Moon size={ICON_PX} aria-hidden="true" />}
              <span>{LIGHTS_TEXT.themes[otherTheme]}</span>
            </button>
            <button type="button" className="d5-lights-btn" onClick={() => controlsRef.current?.recentre()} disabled={!hasNodes} title={LIGHTS_TEXT.recentreHint}>
              <LocateFixed size={ICON_PX} aria-hidden="true" />
              <span>{LIGHTS_TEXT.recentre}</span>
            </button>
          </div>
          <button type="button" className="d5-lights-icon-btn" onClick={onClose} aria-label={LIGHTS_TEXT.close} title={LIGHTS_TEXT.close}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="d5-lights-body">
          {hasNodes ? (
            <>
              <LightsCanvas layout={layout} lights={lights} reducedMotion={reducedMotion} label={canvasLabel(categoryLabel, lights.summary)}
                controlsRef={controlsRef} options={options} themeMode={themeMode} selection={selection} hoverId={hoverId} onSelect={select} />
              <aside className="d5-lights-panel" aria-label={LIGHTS_TEXT.details}>
                <LightsSidebar lights={lights} layout={layout} diagState={diagState} bundle={bundle} options={options} selection={selection}
                  learnedKey={learnedKey} onSelect={select} onHover={setHoverId} />
              </aside>
            </>
          ) : (
            <div className="d5-lights-stage d5-lights-empty">{bundle ? LIGHTS_TEXT.empty : LIGHTS_TEXT.loading}</div>
          )}
        </div>
        <footer className="d5-lights-footer">
          <span className="d5-lights-hint">{LIGHTS_TEXT.hint}</span>
        </footer>
        {lights && <LiveMirror summary={lights.summary} />}
      </div>
    </div>
  );
  return typeof document === "undefined" ? tree : createPortal(tree, document.body);
}
